<template>
  <!-- MBR033/MBR034 My Vouchers — member_app_UI/song_fa_members_rewards_my_vouchers -->
  <div class="page-header align-items-start min-vh-screen mbr-vouchers">
    <span class="mask bg-white"></span>
    <div class="container p-0">
      <header class="mbr-vouchers__bar">
        <div class="mbr-vouchers__head">
          <h1 class="mbr-vouchers__title">{{ $t('reward.title') }}</h1>
          <span v-if="pointChip" class="mbr-vouchers__points">{{ pointChip }}</span>
        </div>
        <!-- My Vouchers is this screen; the other two segments navigate, nothing else changes there -->
        <nav class="mbr-vouchers__segment" :aria-label="$t('reward.sections')">
          <button type="button" class="mbr-vouchers__seg mbr-vouchers__seg--active">
            {{ $t('reward.tab_my_vouchers') }}
          </button>
          <button type="button" class="mbr-vouchers__seg" @click="go('/reward-catalogue')">
            {{ $t('reward.tab_catalogue') }}
          </button>
          <button type="button" class="mbr-vouchers__seg" @click="go('/store')">
            {{ $t('reward.tab_store') }}
          </button>
        </nav>
      </header>

      <main class="mbr-vouchers__body">
        <div class="mbr-vouchers__tools">
          <label class="mbr-vouchers__search">
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path stroke-linecap="round" d="M21 21l-4.35-4.35" /></svg>
            <input
              id="keyword_search"
              type="search"
              name="keyword_search"
              :placeholder="$t('my_rewards.search_placeholder')"
              @input="onChangesearchKeyword"
            />
          </label>
          <!-- Sort / Filter are built here rather than in the shared SortFilterToolbar, which
               Store and RewardCatalogue also render; they still open the same two sheets -->
          <button
            type="button"
            class="mbr-vouchers__tool"
            :aria-label="$t('sort.title')"
            data-bs-toggle="modal"
            data-bs-target="#bottom_modal_sort"
          >
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3 4h18M6 10h12M9 16h6" /></svg>
          </button>
          <button
            type="button"
            class="mbr-vouchers__tool"
            :class="{ 'mbr-vouchers__tool--on': hasFilter }"
            :aria-label="$t('filter.title')"
            data-bs-toggle="modal"
            data-bs-target="#bottom_modal_filter"
          >
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
            <span v-if="hasFilter" class="mbr-vouchers__dot" aria-hidden="true"></span>
          </button>
        </div>

        <div class="mbr-vouchers__chips">
          <button
            type="button"
            class="mbr-vouchers__chip"
            :class="{ 'mbr-vouchers__chip--active': typeChecked == 1 }"
            @click="getMyRewards(1, searchKeyword, params)"
          >
            {{ $t('voucher.new') }}
          </button>
          <button
            type="button"
            class="mbr-vouchers__chip"
            :class="{ 'mbr-vouchers__chip--active': typeChecked == 2 }"
            @click="getMyRewards(2, searchKeyword, params)"
          >
            {{ $t('voucher.used_expired') }}
          </button>
        </div>

        <div v-if="myRewards.length > 0 && loadDataTabDone" class="mbr-vouchers__list">
          <template v-for="(value, index) in myRewards" :key="index">
            <!-- Bundle: one card standing for the vouchers still unused inside it -->
            <div
              v-if="value.is_bundle"
              class="mbr-vouchers__bundle"
              :class="{ 'mbr-vouchers__card--spent': isSpentTab }"
              @click="bundleDetail(value.id, value.member_voucher_bundle_id ?? 0)"
            >
              <div class="mbr-vouchers__bundle-top">
                <div class="mbr-vouchers__pills">
                  <span class="mbr-vouchers__pill">{{ $t('voucher.bundle') }}</span>
                  <span v-if="value.is_new" class="mbr-vouchers__pill mbr-vouchers__pill--dark">{{ $t('voucher.new') }}</span>
                </div>
                <span class="mbr-vouchers__balance">{{ $t('voucher.balance_n', { n: value.number_of_vouchers_unused ?? 0 }) }}</span>
              </div>
              <h3 class="mbr-vouchers__bundle-title">{{ value.reward_title }}</h3>
              <p v-if="value.number_of_vouchers_text" class="mbr-vouchers__bundle-sub">
                {{ $t('voucher.n_in_bundle', { text: voucherCountText(value) }) }}
              </p>
              <div class="mbr-vouchers__bundle-foot">
                <span class="mbr-vouchers__valid">{{ $t('voucher.valid_until', { date: value.expiry_date_text }) }}</span>
                <span class="mbr-vouchers__bundle-cta">{{ $t('voucher.view_bundle') }} &rarr;</span>
              </div>
            </div>

            <!-- Ticket row: tinted stub with the voucher value, body with title and expiry -->
            <div
              v-else
              class="mbr-vouchers__card"
              :class="{ 'mbr-vouchers__card--spent': isSpentTab }"
              @click="getRewardDetail(value.voucher_id, value.expiry_date_text)"
            >
              <div class="mbr-vouchers__stub">
                <span class="mbr-vouchers__notch" aria-hidden="true"></span>
                <span v-if="stubValue(value)" class="mbr-vouchers__stub-value">{{ stubValue(value) }}</span>
                <span v-else class="mbr-vouchers__stub-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></svg>
                </span>
                <span class="mbr-vouchers__stub-label">{{ stubLabel(value) }}</span>
              </div>
              <div class="mbr-vouchers__main">
                <div class="mbr-vouchers__top">
                  <div class="mbr-vouchers__text">
                    <h3 class="mbr-vouchers__name">{{ value.reward_title }}</h3>
                    <p v-if="minSpendText(value)" class="mbr-vouchers__sub">{{ minSpendText(value) }}</p>
                    <p v-else-if="value.bundle_name" class="mbr-vouchers__sub">{{ $t('voucher.from_bundle', { name: value.bundle_name }) }}</p>
                  </div>
                  <span v-if="value.locked" class="mbr-vouchers__tag mbr-vouchers__tag--locked">{{ $t('voucher.locked') }}</span>
                  <span v-else-if="isSpentTab" class="mbr-vouchers__tag">{{ value.use == 0 ? $t('voucher.expired') : $t('voucher.used') }}</span>
                  <span v-else-if="value.is_new" class="mbr-vouchers__tag mbr-vouchers__tag--new">{{ $t('voucher.new') }}</span>
                </div>
                <div class="mbr-vouchers__foot">
                  <span class="mbr-vouchers__valid">{{ $t('voucher.valid_until', { date: value.expiry_date_text }) }}</span>
                  <span class="mbr-vouchers__cta">{{ isSpentTab ? $t('voucher.view') : $t('voucher.redeem') }} &rarr;</span>
                </div>
              </div>
            </div>
          </template>
        </div>
        <div v-if="myRewards.length == 0 && loadDataTabDone" class="mbr-vouchers__empty">
          {{ $t('home.no_rewards') }}
        </div>
      </main>

      <div class="position-fixed top-1 end-1 z-index-2">
        <material-snackbar
          v-if="snackbar === 'danger' || snackbar === 'success'"
          title=""
          date=""
          :description="message"
          :icon="{ component: 'campaign', color: 'white' }"
          :color="snackbar"
          :close-handler="closeSnackbar"
        />
      </div>
    </div>
    <Sort :filterData="filterData" :changeSortType="changeSortType" />
    <Filter
      :filterRewardType="filterRewardType"
      :filterArrRewardType="filterArrRewardType"
      :stores="stores"
      :filterStore="filterStore"
      :filterArrStore="filterArrStore"
      :filterData="filterData"
      :resetData="resetData"
    />
    <material-loading :active="loaderActive" />
    <QrcodeReward
      :rewardDetail="rewardDetail"
      :merchantMember="merchantMember"
      :isShowQr="typeChecked == 2 ? false : true"
    />
    <QrcodeMember />
    <MbrBottomNav />
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import RewardService from "../services/reward.service";
import QrcodeReward from "@/components/QrcodeReward.vue";
import "bootstrap/dist/js/bootstrap.bundle";
import MaterialLoading from "@/components/MaterialLoading.vue";
import Sort from "@/components/Sort.vue";
import Filter from "@/components/Filter.vue";
import QrcodeMember from "@/components/QrcodeMember.vue";
import MbrBottomNav from "@/components/MbrBottomNav.vue";
import HomeService from "../services/home.service";
import "bootstrap/js/dist/modal";
import $ from "jquery";
import { getMerchantCode } from "@/config/utils";
import { voucherCountText } from "@/i18n/apiText";
import { errorMessage } from "@/utils/errorMessage";

export default {
  name: "my-transacions",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    QrcodeReward,
    QrcodeMember,
    MbrBottomNav,
    Sort,
    Filter,
  },
  data() {
    return {
      snackbar: null,
      loaderActive: false,
      merchantCode: "",
      myRewards: [],
      rewardsUsedAndExpired: [],
      typeChecked: 1,
      searchKeyword: "",
      searchTimerId: null,
      rewardDetail: {},
      merchantMember: {},
      stores: [],
      filterArrRewardType: [],
      filterArrStore: [],
      params: {},
      sortType: "1",
      loadDataTabDone: false,
      // points chip in the header; /member/home is the only endpoint carrying the balance
      pointFormat: "",
      rewardType: 0,
      rewardPoint: 2,
      // leading symbol of merchant_member.amount_format ("S$0.00" -> "S$")
      currencySymbol: "$",
      // MintyMerReward::TYPE_* — 1 percentage off, 2 fixed amount off, 3 free item
      typePercentOff: 1,
      typeAmountOff: 2,
    };
  },
  created() {
    this.getMyRewards(1, "", this.params);
    this.getBranches();
    this.getPointBalance();
  },

  // draws its own header like the design: hide the top navbar, and keep the
  // sidenav as an off-screen drawer so MbrBottomNav's ACCOUNT can slide it in
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },

  beforeUnmount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },
  computed: {
    // already formatted by the backend as "8 Point"; hidden for cashback merchants
    pointChip() {
      return this.rewardType == this.rewardPoint ? this.pointFormat : "";
    },

    hasFilter() {
      return this.filterArrRewardType.length > 0 || this.filterArrStore.length > 0;
    },

    isSpentTab() {
      return this.typeChecked == 2;
    },
  },
  mounted() {
    const code = getMerchantCode();
    this.merchantCode = code ? `/${code}` : "";
  },
  methods: {
    voucherCountText,
    ...mapMutations([
      "toggleEveryDisplay",
      "toggleHideConfig",
      "toggleNavbarOnly",
      "navbarMinimize",
    ]),
    ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),
    ...mapState(["isPinned"]),

    closeSnackbar() {
      this.snackbar = null;
    },

    go(path) {
      this.$router.push(this.merchantCode + path);
    },

    // reward_unit / order_total come back as decimals ("100.00000000"), which
    // overflowed the stub; show 100 and 2.5, not 100.00000000 and 2.50000000
    trimDecimals(value) {
      const number = Number(value);
      if (!isFinite(number)) {
        return String(value ?? "");
      }
      return String(parseFloat(number.toFixed(2)));
    },

    // Free-item vouchers carry no unit, so their stub shows an icon instead
    stubValue(voucher) {
      if (voucher.reward_type == this.typePercentOff) {
        return this.trimDecimals(voucher.reward_unit) + "%";
      }
      if (voucher.reward_type == this.typeAmountOff) {
        return this.currencySymbol + this.trimDecimals(voucher.reward_unit);
      }
      return "";
    },

    stubLabel(voucher) {
      return voucher.reward_type == this.typeAmountOff ||
        voucher.reward_type == this.typePercentOff
        ? this.$t('voucher.off')
        : this.$t('voucher.free_item');
    },

    // order_minimum 2 = MintyMerReward::ORDER_MINIMUM_YES ("Order Total")
    minSpendText(voucher) {
      return voucher.order_minimum == 2 && Number(voucher.order_total) > 0
        ? this.$t('reward.min_spend', { amount: this.currencySymbol + this.trimDecimals(voucher.order_total) })
        : "";
    },

    // the chip is cosmetic: a failure here must not block the voucher list
    getPointBalance() {
      HomeService.getDataHomePage().then(
        (response) => {
          if (response.data.result.isSuccess) {
            const merchantMember = response.data.data.merchant_member ?? {};
            this.pointFormat = merchantMember.point_format ?? "";
            this.rewardType = merchantMember.reward_type ?? 0;
            const symbol = (merchantMember.amount_format ?? "").match(/^[^\d]+/);
            if (symbol) {
              this.currencySymbol = symbol[0].trim();
            }
          }
        },
        (error) => {
          console.log(error);
        }
      );
    },

    myRewardDetail(reward_id) {
      this.$router.push(this.merchantCode + "/my-reward-detail/" + reward_id);
    },

    bundleDetail(rewardId, memberVoucherBundleId) {
      this.$router.push(this.merchantCode + "/voucher-bundle/" + rewardId + '?memberVoucherBundleId='+ memberVoucherBundleId + '&isReward=true');
    },

    getRewardDetail(rewardId, expiry_date_text = null) {
      this.showLoader();
      RewardService.getRewardDetail(rewardId, 1).then(
        (response) => {
          if (response.data.result.isSuccess) {
            let rewardData = response.data.data.reward;
            if (expiry_date_text) {
              rewardData.expiry_date_text = expiry_date_text
            }
            this.rewardDetail = rewardData;
            this.merchantMember = response.data.data.merchant_member;
            $(".btn-close").click();
            $(".btn-qrcode-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },

    onChangesearchKeyword(event) {
      this.searchKeyword = event.target.value;
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.getMyRewards(this.typeChecked, this.searchKeyword, this.params);
      }, 1000);
    },

    getMyRewards(type, search_keyword = "", params) {
      this.loadDataTabDone = false;
      this.myRewards = [];
      search_keyword = this.searchKeyword;
      this.typeChecked = type;
      this.showLoader();
      params.with_bundle = 1;

      RewardService.myRewards(type, search_keyword, params).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.myRewards = response.data.data;
            this.loadDataTabDone = true;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },

    changeSortType(event) {
      this.sortType = event.target.value;
    },

    filterRewardType(value) {
      const index = this.filterArrRewardType.indexOf(value);
      if (index > -1) {
        this.filterArrRewardType.splice(index, 1);
      } else {
        this.filterArrRewardType.push(value);
      }
    },

    filterStore(value) {
      const index = this.filterArrStore.indexOf(value);
      if (index > -1) {
        this.filterArrStore.splice(index, 1);
      } else {
        this.filterArrStore.push(value);
      }
    },

    getBranches() {
      this.stores = [];
      this.showLoader();
      RewardService.getBranches().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.stores = response.data.data;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    filterData() {
      $(".btn-close").click();
      $(".x-close").click();
      this.params = {
        sort_type: this.sortType,
        filter_reward_type: this.filterArrRewardType,
        filter_store: this.filterArrStore
      };
      this.getMyRewards(this.typeChecked, this.searchKeyword, this.params);
    },
    resetData() {
      // the chips paint themselves from these arrays now; the old jQuery that
      // forced inline colours on .btn-unclick has nothing left to match
      this.filterArrRewardType = [];
      this.filterArrStore = [];
    },

    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
  },
};
</script>
<style scoped>
/* MBR033 My Vouchers header — member_app_UI/song_fa_members_rewards_my_vouchers */

/* Material Dashboard's `.page-header { overflow: hidden }` would stop the header sticking */
.mbr-vouchers.page-header {
  overflow: visible;
  overflow-x: clip;
  /* room for the fixed MbrBottomNav */
  padding-bottom: 120px;
  font-family: var(--mbr-font-body);
}

/* Store/RewardCatalogue load an unscoped `* { font-family: Poppins }` app-wide;
   :where() keeps this at one attribute's specificity so the class rules below still win */
.mbr-vouchers :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-vouchers .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

/* mobile column, like Home and Account */
.mbr-vouchers .container {
  max-width: 440px;
  margin: 0 auto;
}

.mbr-vouchers__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 24px 20px 12px;
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  border-bottom: 1px solid rgba(var(--mbr-color-outline-rgb), 0.3);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mbr-vouchers__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-vouchers__title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 24px;
  font-weight: 800;
  line-height: 32px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-ink-headline);
}

.mbr-vouchers__points {
  flex: none;
  padding: 4px 10px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-primary);
  white-space: nowrap;
}

.mbr-vouchers__segment {
  display: flex;
  margin-top: 12px;
  padding: 4px;
  background: var(--mbr-color-surface);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-vouchers__seg {
  flex: 1 1 0;
  padding: 8px 4px;
  background: none;
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  color: var(--mbr-color-ink-secondary);
  transition: background 0.15s ease, color 0.15s ease;
}

.mbr-vouchers__seg--active {
  background: var(--mbr-color-primary);
  font-weight: 800;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
}

.mbr-vouchers__body {
  padding: 16px 20px 0;
}

.mbr-vouchers__tools {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mbr-vouchers__search {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 8px 14px;
  background: var(--mbr-color-tint-dark);
  border: 1px solid transparent;
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-ink-secondary);
  transition: border-color 0.15s ease;
}

.mbr-vouchers__search:focus-within {
  border-color: var(--mbr-color-primary);
}

.mbr-vouchers__search svg {
  flex: none;
  width: 16px;
  height: 16px;
  opacity: 0.7;
}

/* outranks the global `input.nosubmit` / `input[type=search]` rules in custom.css */
.mbr-vouchers__search input[type="search"] {
  width: 100%;
  min-width: 0;
  padding: 0;
  background: none;
  border: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: var(--mbr-color-ink-headline);
}

.mbr-vouchers__search input[type="search"]:focus {
  border: 0 !important;
  outline: none;
}

.mbr-vouchers__search input[type="search"]::placeholder {
  color: var(--mbr-color-ink-muted);
  text-align: left;
  opacity: 1;
}

.mbr-vouchers__tool {
  position: relative;
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.7);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-ink-headline);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  transition: transform 0.12s ease;
}

.mbr-vouchers__tool:active {
  transform: scale(0.95);
}

.mbr-vouchers__tool svg {
  width: 16px;
  height: 16px;
}

.mbr-vouchers__tool--on {
  border-color: var(--mbr-color-primary);
  color: var(--mbr-color-primary);
}

.mbr-vouchers__dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 7px;
  height: 7px;
  background: var(--mbr-color-primary);
  border: 1.5px solid var(--mbr-color-surface);
  border-radius: var(--mbr-radius-full);
}

.mbr-vouchers__chips {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.mbr-vouchers__chip {
  padding: 6px 16px;
  background: var(--mbr-color-tint-dark);
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
  transition: background 0.15s ease, color 0.15s ease;
}

.mbr-vouchers__chip--active {
  background: var(--mbr-color-primary);
  font-weight: 800;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
}

.mbr-vouchers__list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
}

.mbr-vouchers__card {
  display: flex;
  overflow: hidden;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: var(--mbr-radius-lg);
  box-shadow: var(--mbr-shadow-card);
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.mbr-vouchers__card:hover {
  border-color: rgba(var(--mbr-color-primary-rgb), 0.4);
}

/* used / expired rows read as spent without hiding anything */
.mbr-vouchers__card--spent {
  opacity: 0.6;
}

.mbr-vouchers__stub {
  position: relative;
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 96px;
  padding: 12px;
  background: var(--mbr-color-tint);
  border-right: 1px dashed var(--mbr-color-outline);
}

/* the ticket bite: a canvas-coloured disc straddling the dashed rule */
.mbr-vouchers__notch {
  position: absolute;
  top: 50%;
  right: -10px;
  width: 20px;
  height: 20px;
  background: var(--mbr-color-canvas);
  border-radius: var(--mbr-radius-full);
  box-shadow: inset 1px 0 0 var(--mbr-color-outline);
  transform: translateY(-50%);
}

.mbr-vouchers__stub-value {
  text-align: center;
  max-width: 100%;
  overflow-wrap: anywhere;
  font-family: var(--mbr-font-headline);
  font-size: 20px;
  font-weight: 800;
  line-height: 26px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-primary);
}

.mbr-vouchers__stub-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-primary);
}

.mbr-vouchers__stub-icon svg {
  width: 20px;
  height: 20px;
}

.mbr-vouchers__stub-label {
  font-size: 10px;
  font-weight: 700;
  line-height: 13px;
  letter-spacing: 0.08em;
  color: var(--mbr-color-ink-secondary);
  text-align: center;
  text-transform: uppercase;
}

.mbr-vouchers__main {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  padding: 14px 14px 14px 16px;
}

.mbr-vouchers__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.mbr-vouchers__text {
  min-width: 0;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-vouchers__name {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 15px;
  font-weight: 800;
  line-height: 20px;
  letter-spacing: normal;
  color: var(--mbr-color-ink-headline);
}

.mbr-vouchers__sub {
  margin: 2px 0 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-vouchers__tag {
  flex: none;
  padding: 2px 8px;
  background: var(--mbr-color-tint-dark);
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
  white-space: nowrap;
}

.mbr-vouchers__tag--new {
  background: var(--mbr-color-peach);
  color: var(--mbr-color-primary);
}

.mbr-vouchers__tag--locked {
  background: var(--mbr-color-error-container);
  color: var(--mbr-color-error);
}

.mbr-vouchers__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--mbr-color-tint-dark);
}

.mbr-vouchers__valid {
  font-size: 11px;
  font-weight: 500;
  line-height: 15px;
  color: var(--mbr-color-ink-muted);
}

.mbr-vouchers__cta {
  flex: none;
  font-size: 11px;
  font-weight: 700;
  line-height: 15px;
  color: var(--mbr-color-primary);
}

/* Bundle card — the design's second voucher variant */
.mbr-vouchers__bundle {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: linear-gradient(135deg, var(--mbr-color-canvas), var(--mbr-color-tint));
  border: 2px solid rgba(var(--mbr-color-primary-rgb), 0.2);
  border-radius: var(--mbr-radius-lg);
  box-shadow: var(--mbr-shadow-card);
  cursor: pointer;
}

.mbr-vouchers__bundle-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.mbr-vouchers__pills {
  display: flex;
  gap: 6px;
}

.mbr-vouchers__pill {
  padding: 2px 8px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 10px;
  font-weight: 800;
  line-height: 14px;
  letter-spacing: 0.08em;
  color: var(--mbr-color-primary);
  text-transform: uppercase;
}

.mbr-vouchers__pill--dark {
  background: var(--mbr-color-primary);
  color: var(--mbr-color-on-primary);
}

.mbr-vouchers__balance {
  flex: none;
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
  color: var(--mbr-color-primary);
}

.mbr-vouchers__bundle-title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 16px;
  font-weight: 800;
  line-height: 22px;
  letter-spacing: normal;
  color: var(--mbr-color-ink-headline);
}

.mbr-vouchers__bundle-sub {
  margin: -6px 0 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-vouchers__bundle-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
}

.mbr-vouchers__bundle-cta {
  flex: none;
  padding: 6px 14px;
  border: 1.5px solid var(--mbr-color-primary);
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
  color: var(--mbr-color-primary);
}

.mbr-vouchers__empty {
  margin-top: 16px;
  padding: 40px 20px;
  background: var(--mbr-color-tint);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: var(--mbr-radius-lg);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--mbr-color-ink-secondary);
  text-align: center;
}
</style>
